/**
 * ProgressService - Manages user learning progress
 * 
 * Tracks lesson completion, points, and overall progress statistics.
 */

import { storageService } from './StorageService';
import { configService } from './ConfigService';

class ProgressService {
  /**
   * Mark a lesson as completed and award points
   */
  completeLesson(lessonId, options = {}) {
    const { isFirstTry = false, isPerfectScore = false, timeTaken = 0 } = options;
    
    // Check if already completed
    const wasCompleted = storageService.isLessonCompleted(lessonId);
    
    // Mark as completed
    storageService.markLessonCompleted(lessonId);
    
    // Award points (only if first time completing)
    let pointsEarned = 0;
    if (!wasCompleted) {
      const gamification = configService.getGamification();
      pointsEarned = gamification.pointsPerLesson || 100;
      
      // Bonus points
      if (isFirstTry) {
        pointsEarned += gamification.firstTryBonus || 25;
      }
      if (isPerfectScore) {
        pointsEarned += gamification.perfectLessonBonus || 50;
      }
      
      storageService.addPoints(pointsEarned);
      
      // Update statistics
      storageService.incrementStatistic('totalLessonsCompleted');
    }
    
    // Record the time
    storageService.saveLessonProgress(lessonId, -1, true);
    storageService.recordActivity();
    
    return {
      lessonId,
      pointsEarned,
      totalPoints: storageService.getTotalPoints(),
      wasFirstCompletion: !wasCompleted
    };
  }

  /**
   * Save current progress within a lesson
   */
  saveLessonProgress(lessonId, currentStep) {
    storageService.saveLessonProgress(lessonId, currentStep, false);
    storageService.recordActivity();
  }

  /**
   * Get progress for a specific lesson
   */
  getLessonProgress(lessonId) {
    return storageService.getLessonProgress(lessonId);
  }

  /**
   * Mark a challenge as completed
   */
  completeChallenge(challengeId, options = {}) {
    const { timeTaken = 0, attempts = 1 } = options;
    
    const wasCompleted = storageService.isChallengeCompleted(challengeId);
    storageService.markChallengeCompleted(challengeId);
    
    let pointsEarned = 0;
    if (!wasCompleted) {
      const gamification = configService.getGamification();
      pointsEarned = gamification.pointsPerChallenge || 50;
      
      storageService.addPoints(pointsEarned);
      storageService.incrementStatistic('totalChallengesCompleted');
    }
    
    storageService.recordActivity();
    
    return {
      challengeId,
      pointsEarned,
      totalPoints: storageService.getTotalPoints(),
      wasFirstCompletion: !wasCompleted
    };
  }

  /**
   * Award points for completing a quiz
   */
  completeQuiz(quizId, score, totalQuestions) {
    const gamification = configService.getGamification();
    const basePoints = gamification.pointsPerQuiz || 25;
    const percentage = (score / totalQuestions) * 100;
    
    // Scale points by score
    const pointsEarned = Math.round(basePoints * (percentage / 100));
    storageService.addPoints(pointsEarned);
    storageService.recordActivity();
    
    return {
      quizId,
      score,
      totalQuestions,
      percentage,
      pointsEarned,
      totalPoints: storageService.getTotalPoints()
    };
  }

  /**
   * Award points for practice
   */
  completePractice() {
    const gamification = configService.getGamification();
    const pointsEarned = gamification.pointsPerPractice || 10;
    
    storageService.addPoints(pointsEarned);
    storageService.incrementStatistic('totalCodeExecutions');
    storageService.recordActivity();
    
    return {
      pointsEarned,
      totalPoints: storageService.getTotalPoints()
    };
  }

  /**
   * Add points directly (used for bonus/special rewards)
   */
  addPoints(points) {
    storageService.addPoints(points);
    storageService.recordActivity();
    return {
      pointsEarned: points,
      totalPoints: storageService.getTotalPoints()
    };
  }

  /**
   * Get overall progress statistics
   */
  getOverallProgress() {
    const lessons = configService.getLessonsList();
    const challenges = configService.getChallengesList();
    const completedLessons = storageService.getCompletedLessons();
    const completedChallenges = storageService.getCompletedChallenges();
    const totalPoints = storageService.getTotalPoints();
    const statistics = storageService.getStatistics();
    
    // Calculate percentages
    const lessonProgress = lessons.length > 0 
      ? Math.round((completedLessons.length / lessons.length) * 100) 
      : 0;
    const challengeProgress = challenges.length > 0 
      ? Math.round((completedChallenges.length / challenges.length) * 100) 
      : 0;
    
    // Get current level
    const currentLevel = configService.getLevelForPoints(totalPoints);
    const pointsToNextLevel = configService.getPointsForNextLevel(totalPoints);
    
    return {
      totalPoints,
      currentLevel,
      pointsToNextLevel,
      
      lessons: {
        completed: completedLessons.length,
        total: lessons.length,
        percentage: lessonProgress
      },
      
      challenges: {
        completed: completedChallenges.length,
        total: challenges.length,
        percentage: challengeProgress
      },
      
      statistics: {
        ...statistics,
        totalLessonsCompleted: completedLessons.length,
        totalChallengesCompleted: completedChallenges.length
      }
    };
  }

  /**
   * Get progress by category
   */
  getProgressByCategory() {
    const categories = configService.getCategories();
    const completedLessons = storageService.getCompletedLessons();
    
    return categories.map(category => {
      const lessonsInCategory = configService.getLessonsByCategory(category.id);
      const completedInCategory = lessonsInCategory.filter(
        lesson => completedLessons.includes(lesson.id)
      );
      
      return {
        id: category.id,
        title: category.title,
        icon: category.icon,
        color: category.color,
        completed: completedInCategory.length,
        total: lessonsInCategory.length,
        percentage: lessonsInCategory.length > 0
          ? Math.round((completedInCategory.length / lessonsInCategory.length) * 100)
          : 0
      };
    });
  }

  /**
   * Check if user has completed daily goal
   */
  checkDailyGoal() {
    const gamification = configService.getGamification();
    const dailyGoal = gamification.dailyGoal || 3;
    
    // Get today's completed lessons
    const today = new Date().toDateString();
    const lessonProgress = storageService.get('lesson_progress', {});
    
    let completedToday = 0;
    for (const lessonId in lessonProgress) {
      const progress = lessonProgress[lessonId];
      if (progress.completed && progress.timestamp) {
        const completedDate = new Date(progress.timestamp).toDateString();
        if (completedDate === today) {
          completedToday++;
        }
      }
    }
    
    return {
      completed: completedToday,
      goal: dailyGoal,
      achieved: completedToday >= dailyGoal,
      remaining: Math.max(0, dailyGoal - completedToday)
    };
  }

  /**
   * Reset all progress (for testing or user request)
   */
  resetProgress() {
    storageService.set('completed_lessons', []);
    storageService.set('completed_challenges', []);
    storageService.set('lesson_progress', {});
    storageService.set('total_points', 0);
    storageService.set('user_statistics', {
      totalLessonsCompleted: 0,
      totalChallengesCompleted: 0,
      totalCodeExecutions: 0,
      totalTimeSpent: 0,
      firstUseDate: Date.now(),
      lastActiveDate: Date.now()
    });
  }
}

// Export singleton instance
export const progressService = new ProgressService();
export default progressService;